/**
 * Mulberry32 — small, fast, deterministic PRNG. No Math.random anywhere in sim.
 * Seed each (trackId, carId, lapStartTick, serverBuildHash) tuple.
 */
export class Rng {
  private state: number;

  constructor(seed: number) {
    // Avoid degenerate zero seed.
    this.state = seed >>> 0 || 0x6d2b79f5;
  }

  next(): number {
    let t = (this.state = (this.state + 0x6d2b79f5) >>> 0);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Inclusive lo, exclusive hi. */
  range(lo: number, hi: number): number {
    return lo + (hi - lo) * this.next();
  }

  pick<T>(arr: readonly T[]): T {
    if (arr.length === 0) throw new Error('Rng.pick on empty array');
    const idx = Math.floor(this.next() * arr.length);
    // Math.floor ensures idx < arr.length when next() < 1; idx is also >= 0.
    return arr[idx] as T;
  }
}

/**
 * Deterministic seed derived from a tuple of stable identifiers.
 * Use this for any sim-influencing randomness (boost particle directions,
 * AI jitter, etc.) — never reseed mid-frame.
 */
export const deriveSeed = (...parts: (number | string)[]): number => {
  let h = 0x811c9dc5;
  for (const p of parts) {
    const s = typeof p === 'number' ? p.toString(36) : p;
    for (let i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 0x01000193);
    }
  }
  return h >>> 0;
};
